/**
 * Generate rental credentials email HTML from template configuration
 * @param {Object} templateConfig - Template configuration from EmailTemplate model
 * @param {Object} data - Email data
 * @param {string} language - Language code ('es' or 'en')
 * @returns {string} HTML email content
 */

const UI_LABELS = {
  es: {
    confirmed: 'Reserva confirmada',
    booking: 'Reserva',
    date: 'Fecha',
    location: 'Ubicaci&#243;n',
    zone: 'ZONA',
    locker: 'Taquilla',
    howToAccess: 'C&#243;mo acceder',
    step1: 'Localiza tu taquilla n&#250;mero',
    step2: 'Toca el teclado',
    step3: 'Introduce tu PIN',
    step4_normal: 'Accede las veces que necesites durante el d&#237;a',
    step4_mode3: 'Abre una vez para guardar tus cosas. Una vez cierres, tendr&#225;s una apertura m&#225;s para sacarlas',
    access: 'Acceso',
    access_sub: 'al local',
    entry_code: 'C&#243;digo de entrada',
    greeting_default: '&#161;Hola {name}!',
    intro_default: 'Aqu&#237; tienes los detalles de tu taquilla',
    footer_default: '&#191;Necesitas el PIN en f&#237;sico? Contacta con el responsable de taquillas y mu&#233;strale este email.',
    privacy_policy: 'Pol&#237;tica de privacidad',
  },
  en: {
    confirmed: 'Booking confirmed',
    booking: 'Booking',
    date: 'Date',
    location: 'Location',
    zone: 'ZONE',
    locker: 'Locker',
    howToAccess: 'How to access',
    step1: 'Find your locker number',
    step2: 'Touch the keypad',
    step3: 'Enter your PIN',
    step4_normal: 'Access as many times as you need during the day',
    step4_mode3: 'Open once to store your things. Once you close, you&#39;ll have one more opening to take them out',
    access: 'Access',
    access_sub: 'to venue',
    entry_code: 'Entry code',
    greeting_default: 'Hello {name}!',
    intro_default: 'Here are your locker details',
    footer_default: 'Need the PIN in physical form? Contact the locker manager and show them this email.',
    privacy_policy: 'Privacy policy',
  },
};

export function generateRentalCredentialsEmail(templateConfig, data, language = 'es') {
  const {
    name,
    code,
    startDate,
    endDate,
    zoneNumber,
    zoneDescription,
    lockersInfoHtml,
    zoneLink,
    lockerMatricula,
    lockerPin,
    lockerInvoice,
    date,
    lockerCustomer,
    zoneSelected,
    descriptionZone,
    doorAccess,
    isMode3,
  } = data;

  // UI labels (non-customizable, language-based)
  const ui = UI_LABELS[language] || UI_LABELS.es;

  // Customer-customizable texts (from template config, fall back to ui defaults)
  const lang = templateConfig[language] || templateConfig.es || {};

  // Customizable values from template
  const cardBgColor = templateConfig.primary_color || '#004d98';
  const accentColor = templateConfig.secondary_color || '#ffc905';
  const headerBgColor = templateConfig.header_bg_color || '#001d3a';
  const headerImageSrc = templateConfig.header_image_base64 || templateConfig.header_image_url || '';
  const headerLink = templateConfig.header_link || 'https://vacway.com';
  const fontFamily = templateConfig.font_family || "'Manrope',Arial,sans-serif";

  // Resolve display values (support both new and legacy field names)
  const displayInvoice = lockerInvoice || code || '';
  const displayDate = date || startDate || '';
  const displayCustomer = lockerCustomer || name || '';
  const displayZone = zoneSelected || zoneNumber || '';
  const displayZoneDesc = descriptionZone || zoneDescription || '';
  const displayMatricula = lockerMatricula || '';
  const displayPin = lockerPin || '';

  const greetingText = (lang.greeting_text || ui.greeting_default).replace('{name}', displayCustomer);
  const introText = lang.intro_text || ui.intro_default;
  const footerInfoText = lang.footer_info_text || ui.footer_default;
  const footerContactText = lang.footer_contact_text || '';
  const legalText = lang.legal_text || '';
  const companyInfo = templateConfig.company_info || '';
  const privacyPolicyUrl = templateConfig.privacy_policy_url || '';

  const hasDoor = doorAccess && doorAccess !== 0 && doorAccess !== '' && doorAccess !== '0';
  const lockerCardWidth = hasDoor ? '48%' : '100%';
  const step4 = isMode3
    ? (lang.step4_mode3 || ui.step4_mode3)
    : (lang.step4_normal || ui.step4_normal);

  const doorCardHtml = hasDoor ? `
      <td class="card-spacer" width="4%" style="padding:0;">&nbsp;</td>
      <td class="card-col" width="48%" style="padding:0;">
        <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation" style="background-color:${cardBgColor};border-radius:12px;">
        <tr>
          <td align="center" style="padding:20px 16px;">
            <p style="margin:0 0 4px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:rgba(255,255,255,0.5);letter-spacing:0.12em;text-transform:uppercase;line-height:1;">${ui.access}</p>
            <p style="margin:0 0 8px 0;font-family:${fontFamily};font-size:10px;font-weight:300;color:rgba(255,255,255,0.5);line-height:1;">${ui.access_sub}</p>
            <p style="margin:0;font-family:${fontFamily};font-size:36px;font-weight:800;color:#ffffff;letter-spacing:0.08em;line-height:1;">${doorAccess}</p>
            <table border="0" cellpadding="0" cellspacing="0" width="30" role="presentation" align="center" style="margin:12px auto;"><tr><td height="1" style="height:1px;line-height:1px;font-size:1px;background-color:rgba(255,255,255,0.15);">&nbsp;</td></tr></table>
            <p style="margin:0;font-family:${fontFamily};font-size:10px;font-weight:300;color:rgba(255,255,255,0.7);letter-spacing:0.08em;line-height:1.3;">${ui.entry_code}</p>
          </td>
        </tr>
        </table>
      </td>` : '';

  // Social links for footer
  const socialItems = [
    templateConfig.social_facebook && `<td style="padding-left:8px;" valign="middle"><a href="${templateConfig.social_facebook}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;"><img src="https://vacway-images.s3.eu-central-1.amazonaws.com/email/facebook2x.png" alt="Facebook" width="28" height="28" style="display:block;width:28px;height:28px;" /></a></td>`,
    templateConfig.social_instagram && `<td style="padding-left:8px;" valign="middle"><a href="${templateConfig.social_instagram}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;"><img src="https://vacway-images.s3.eu-central-1.amazonaws.com/email/instagram2x.png" alt="Instagram" width="28" height="28" style="display:block;width:28px;height:28px;" /></a></td>`,
    templateConfig.social_linkedin && `<td style="padding-left:8px;" valign="middle"><a href="${templateConfig.social_linkedin}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;"><img src="https://vacway-images.s3.eu-central-1.amazonaws.com/email/linkedin2x.png" alt="LinkedIn" width="28" height="28" style="display:block;width:28px;height:28px;" /></a></td>`,
    templateConfig.social_tiktok && `<td style="padding-left:8px;" valign="middle"><a href="${templateConfig.social_tiktok}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;"><img src="https://vacway-images.s3.eu-central-1.amazonaws.com/email/tiktok2x.png" alt="TikTok" width="28" height="28" style="display:block;width:28px;height:28px;" /></a></td>`,
    templateConfig.social_twitter && `<td style="padding-left:8px;" valign="middle"><a href="${templateConfig.social_twitter}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;"><img src="https://vacway-images.s3.eu-central-1.amazonaws.com/email/twitter2x.png" alt="Twitter" width="28" height="28" style="display:block;width:28px;height:28px;" /></a></td>`,
  ].filter(Boolean).join('');

  return `<!DOCTYPE html>
<html lang="${language}" xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="format-detection" content="telephone=no, date=no, address=no, email=no">
  <meta name="x-apple-disable-message-reformatting">
  <title>${ui.confirmed} &#8211; Vacway</title>
  <style type="text/css">
    body,table,td,a{-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}
    table,td{mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:collapse;}
    img{-ms-interpolation-mode:bicubic;border:0;outline:none;text-decoration:none;display:block;}
    body{margin:0;padding:0;width:100% !important;background-color:#f0f0f0;}
    @media only screen and (max-width:500px){
      .email-wrapper{width:100% !important;border-radius:0 !important;}
      .card-col{display:block !important;width:100% !important;padding:0 24px 12px 24px !important;}
      .card-spacer{display:none !important;}
    }
  </style>
</head>
<body style="margin:0;padding:20px 0;background-color:#f0f0f0;font-family:${fontFamily};">
<table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
<tr><td align="center" style="padding:20px 10px;">
<table class="email-wrapper" border="0" cellpadding="0" cellspacing="0" width="480"
  style="max-width:480px;width:100%;background-color:#ffffff;border-radius:16px;border:1px solid #e8e8e8;overflow:hidden;"
  role="presentation">

<!-- HEADER -->
<tr>
  <td valign="top" style="padding:0;background-color:${headerBgColor};">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
    <tr><td style="padding:32px;">
      <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
        <tr>
          <td align="center" style="padding-bottom:24px;">
            ${headerImageSrc ? `<a href="${headerLink}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;line-height:0;"><img src="${headerImageSrc}" alt="Logo" width="160" style="display:block;max-width:160px;height:auto;" /></a>` : ''}
          </td>
        </tr>
        <tr>
          <td align="left">
            <p style="margin:0 0 12px 0;font-family:${fontFamily};font-size:10px;font-weight:700;letter-spacing:0.12em;color:#ffc905;text-transform:uppercase;line-height:1;">${ui.confirmed}</p>
            <p style="margin:0 0 4px 0;font-family:${fontFamily};font-size:20px;font-weight:700;color:#ffffff;line-height:1.2;">${greetingText}</p>
            <p style="margin:0;font-family:${fontFamily};font-size:13px;font-weight:400;color:rgba(255,255,255,0.9);line-height:1.4;">${introText}</p>
          </td>
        </tr>
      </table>
    </td></tr>
    </table>
  </td>
</tr>

<!-- INFO BAND: Booking + Date -->
<tr>
  <td style="padding:0;background-color:#f7f8fa;border-bottom:1px solid #eeeeee;">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
    <tr valign="top">
      <td width="50%" style="padding:16px 16px 16px 32px;">
        <p style="margin:0 0 2px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:#888888;text-transform:uppercase;letter-spacing:0.08em;line-height:1;">${ui.booking}</p>
        <p style="margin:0;font-family:${fontFamily};font-size:11px;font-weight:700;color:#010508;letter-spacing:0.02em;line-height:1.3;">${displayInvoice}</p>
      </td>
      <td width="50%" align="right" style="padding:16px 32px 16px 16px;">
        <p style="margin:0 0 2px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:#888888;text-transform:uppercase;letter-spacing:0.08em;line-height:1;">${ui.date}</p>
        <p style="margin:0;font-family:${fontFamily};font-size:11px;font-weight:700;color:#010508;line-height:1.3;">${displayDate}</p>
      </td>
    </tr>
    </table>
  </td>
</tr>

<!-- INFO BAND: Location -->
<tr>
  <td style="padding:8px 32px 12px 32px;background-color:#f7f8fa;border-bottom:1px solid #eeeeee;">
    <p style="margin:0 0 2px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:#888888;text-transform:uppercase;letter-spacing:0.08em;line-height:1;">${ui.location}</p>
    <p style="margin:0;font-family:${fontFamily};font-size:11px;font-weight:700;color:#010508;line-height:1.3;">${displayCustomer} &#183; ${ui.zone} ${displayZone} &#8211; ${displayZoneDesc}</p>
  </td>
</tr>

<!-- CARDS: Locker + PIN | Door access -->
<tr>
  <td style="padding:24px 32px;">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
    <tr valign="top">
      <td class="card-col" width="${lockerCardWidth}" style="padding:0;">
        <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation" style="background-color:${cardBgColor};border-radius:12px;">
        <tr>
          <td align="center" style="padding:20px 16px;">
            <p style="margin:0 0 8px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:rgba(255,255,255,0.5);letter-spacing:0.12em;text-transform:uppercase;line-height:1;">${ui.locker}</p>
            <p style="margin:0;font-family:${fontFamily};font-size:48px;font-weight:800;color:#ffffff;line-height:1;">${displayMatricula}</p>
            <table border="0" cellpadding="0" cellspacing="0" width="30" role="presentation" align="center" style="margin:12px auto;"><tr><td height="1" style="height:1px;line-height:1px;font-size:1px;background-color:rgba(255,255,255,0.15);">&nbsp;</td></tr></table>
            <p style="margin:0 0 4px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:rgba(255,255,255,0.5);letter-spacing:0.12em;text-transform:uppercase;line-height:1;">PIN</p>
            <p style="margin:0;font-family:${fontFamily};font-size:28px;font-weight:800;color:${accentColor};letter-spacing:0.15em;line-height:1;">${displayPin}</p>
          </td>
        </tr>
        </table>
      </td>
      ${doorCardHtml}
    </tr>
    </table>
  </td>
</tr>

<!-- INSTRUCTIONS -->
<tr>
  <td style="padding:0 32px 24px 32px;">
    <p style="margin:0 0 12px 0;font-family:${fontFamily};font-size:10px;font-weight:500;color:#888888;text-transform:uppercase;letter-spacing:0.12em;line-height:1;">${ui.howToAccess}</p>

    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation" style="margin-bottom:8px;"><tr valign="middle"><td width="24" style="padding-right:12px;" valign="middle"><table border="0" cellpadding="0" cellspacing="0" width="24" role="presentation"><tr><td width="24" height="24" align="center" valign="middle" style="width:24px;height:24px;border-radius:50%;background-color:${cardBgColor};font-family:${fontFamily};font-size:11px;font-weight:700;color:#ffffff;line-height:24px;text-align:center;">1</td></tr></table></td><td style="font-family:${fontFamily};font-size:13px;font-weight:300;color:#444444;line-height:1.5;" valign="middle">${ui.step1} <strong style="font-weight:800;color:#010508;">${displayMatricula}</strong></td></tr></table>

    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation" style="margin-bottom:8px;"><tr valign="middle"><td width="24" style="padding-right:12px;" valign="middle"><table border="0" cellpadding="0" cellspacing="0" width="24" role="presentation"><tr><td width="24" height="24" align="center" valign="middle" style="width:24px;height:24px;border-radius:50%;background-color:${cardBgColor};font-family:${fontFamily};font-size:11px;font-weight:700;color:#ffffff;line-height:24px;text-align:center;">2</td></tr></table></td><td style="font-family:${fontFamily};font-size:13px;font-weight:300;color:#444444;line-height:1.5;" valign="middle">${ui.step2}</td></tr></table>

    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation" style="margin-bottom:8px;"><tr valign="middle"><td width="24" style="padding-right:12px;" valign="middle"><table border="0" cellpadding="0" cellspacing="0" width="24" role="presentation"><tr><td width="24" height="24" align="center" valign="middle" style="width:24px;height:24px;border-radius:50%;background-color:${cardBgColor};font-family:${fontFamily};font-size:11px;font-weight:700;color:#ffffff;line-height:24px;text-align:center;">3</td></tr></table></td><td style="font-family:${fontFamily};font-size:13px;font-weight:300;color:#444444;line-height:1.5;" valign="middle">${ui.step3} <strong style="font-weight:800;color:#010508;">${displayPin}</strong></td></tr></table>

    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation" style="margin-bottom:16px;"><tr valign="middle"><td width="24" style="padding-right:12px;" valign="middle"><table border="0" cellpadding="0" cellspacing="0" width="24" role="presentation"><tr><td width="24" height="24" align="center" valign="middle" style="width:24px;height:24px;border-radius:50%;background-color:${cardBgColor};font-family:${fontFamily};font-size:11px;font-weight:700;color:#ffffff;line-height:24px;text-align:center;">4</td></tr></table></td><td style="font-family:${fontFamily};font-size:13px;font-weight:300;color:#444444;line-height:1.5;" valign="middle">${step4}</td></tr></table>

    <!-- Callout -->
    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
    <tr>
      <td width="3" valign="top" style="width:3px;background-color:#ffc905;line-height:0;font-size:0;">&nbsp;</td>
      <td style="background-color:#fffbea;padding:12px 16px;border-radius:0 8px 8px 0;">
        <p style="margin:0;font-family:${fontFamily};font-size:12px;font-weight:300;color:#555555;line-height:1.6;">${footerInfoText}</p>
      </td>
    </tr>
    </table>

    ${footerContactText ? `<p style="margin:12px 0 0 0;font-family:${fontFamily};font-size:12px;color:#666666;line-height:1.5;">${footerContactText}</p>` : ''}
    ${legalText ? `<p style="margin:16px 0 0 0;font-family:${fontFamily};font-size:10px;color:#aaaaaa;line-height:1.5;">${legalText.replace(/\n/g, '<br>')}</p>` : ''}
    ${privacyPolicyUrl ? `<p style="margin:8px 0 0 0;font-family:${fontFamily};font-size:10px;"><a href="${privacyPolicyUrl}" target="_blank" style="color:${cardBgColor};text-decoration:underline;">${ui.privacy_policy}</a></p>` : ''}
  </td>
</tr>

<!-- FOOTER -->
<tr>
  <td style="padding:0;background:linear-gradient(135deg,${headerBgColor} 0%,${cardBgColor} 50%,#0b97ff 100%);">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation">
    <tr valign="middle">
      <td style="padding:16px 16px 16px 32px;" valign="middle">
        ${headerImageSrc ? `<a href="${headerLink}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;line-height:0;"><img src="${headerImageSrc}" alt="Logo" width="110" style="display:block;max-width:110px;height:auto;" /></a>` : ''}
        ${companyInfo ? `<p style="margin:4px 0 0 0;font-family:${fontFamily};font-size:10px;color:rgba(255,255,255,0.5);line-height:1;">${companyInfo.replace(/\n/g, ' ')}</p>` : ''}
      </td>
      ${socialItems ? `
      <td align="right" style="padding:16px 32px 16px 16px;" valign="middle">
        <table border="0" cellpadding="0" cellspacing="0" role="presentation"><tr valign="middle">${socialItems}</tr></table>
      </td>` : ''}
    </tr>
    </table>
  </td>
</tr>

</table>
</td></tr>
</table>
</body>
</html>`;
}

export default generateRentalCredentialsEmail;